import { useState } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  Chip,
  Collapse,
  alpha,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ValidationError, getFieldDisplayName } from '@/lib/validationErrors';

interface ValidationErrorPanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Array of validation errors to display */
  errors: ValidationError[];
  /**
   * The form's JSON schema, when the view has one. Errors name fields by their DTO name
   * (`ownerOrgUnitId`), but the user knows them by the label on the input ("Planning Org Unit") — which
   * is the schema's `title`. Without it the chips fall back to a humanised DTO name ("Owner Org Unit Id").
   */
  schema?: { properties?: Record<string, { title?: string }> };
}

/** The label the user sees on the input, falling back to a humanised field name. */
function fieldLabel(field: string, schema?: ValidationErrorPanelProps['schema']): string {
  return schema?.properties?.[field]?.title ?? getFieldDisplayName(field);
}

/**
 * Single error entry with collapsible technical details
 */
function ErrorEntry({ fieldErrors, schema, t }: {
  fieldErrors: ValidationError[];
  schema?: ValidationErrorPanelProps['schema'];
  t: (key: string, opts?: any) => string;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Show details toggle if any error carries a rawError object
  const hasDetails = fieldErrors.some((e) => e.rawError && Object.keys(e.rawError).length > 0);

  // A rule may span several fields (a composite unique key names all of them). Chip every field it
  // touches, so the entry says which combination is at fault rather than just the first field.
  const involvedFields = Array.from(
    new Set(fieldErrors.flatMap((e) => (e.members?.length ? e.members : [e.field]))),
  );

  return (
    <ListItem
      sx={{
        flexDirection: 'column',
        alignItems: 'stretch',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {involvedFields.map((involvedField) => (
            <Chip
              key={involvedField}
              label={fieldLabel(involvedField, schema)}
              size="small"
              color="error"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          ))}
          {fieldErrors.length > 1 && (
            <Chip
              label={t('validation.errorCount', { count: fieldErrors.length })}
              size="small"
              color="error"
              variant="filled"
            />
          )}
          {hasDetails && (
            <IconButton
              size="small"
              onClick={() => setDetailsOpen((prev) => !prev)}
              sx={{
                ml: 'auto',
                p: 0.25,
                color: 'text.secondary',
                transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
              aria-label={detailsOpen ? t('validation.hideDetails') : t('validation.showDetails')}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {fieldErrors.map((error, index) => (
          <Box key={index} sx={{ mb: index < fieldErrors.length - 1 ? 1 : 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'error.main',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {error.message}
            </Typography>
            {error.rawError && Object.keys(error.rawError).length > 0 && (
              <Collapse in={detailsOpen}>
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                    mt: 0.5,
                    p: 1,
                    // Theme-aware subtle wash (grey.100 stays light in dark mode).
                    backgroundColor: 'action.hover',
                    borderRadius: 0.5,
                  }}
                >
                  {JSON.stringify(error.rawError, null, 2)}
                </Typography>
              </Collapse>
            )}
          </Box>
        ))}
      </Box>
    </ListItem>
  );
}

/**
 * Validation Error Panel
 * Right-side drawer panel showing detailed validation errors
 * Similar to JsonDebugPanel but for validation errors
 */
export function ValidationErrorPanel({ open, onClose, errors, schema }: ValidationErrorPanelProps) {
  const { t } = useTranslation();
  // Show toggle button if there are errors, even when closed
  // Show panel if open is true, even if errors array is empty (allows for programmatic opening)
  const hasErrors = errors && errors.length > 0;
  
  // Don't show toggle button if no errors and panel is closed
  if (!hasErrors && !open) {
    return null;
  }

  // Group errors by field (in case multiple errors for same field)
  const errorsByField = (errors || []).reduce((acc, error) => {
    if (!acc[error.field]) {
      acc[error.field] = [];
    }
    acc[error.field].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);
  
  const errorEntries = Object.entries(errorsByField);

  const toggleDrawer = () => {
    onClose();
  };

  return (
    <>
      {/* Toggle Button - Always visible when collapsed */}
      {!open && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            // error.light is a bright salmon that glows against the dark shell — use the deep
            // error tone there; light mode keeps its original look.
            backgroundColor: theme.palette.mode === 'dark'
              ? theme.palette.error.dark
              : theme.palette.error.light,
            cursor: 'pointer',
            borderLeft: '1px solid',
            borderColor: 'divider',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: theme.palette.error.main,
            },
          })}
          onClick={toggleDrawer}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleDrawer();
            }}
            sx={{
              color: 'error.contrastText',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.8,
              },
            }}
            size="small"
            aria-label={t('validation.togglePanel')}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={toggleDrawer}
        variant="persistent"
        sx={{
          width: open ? 400 : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 400,
            boxSizing: 'border-box',
            position: 'relative',
            height: '100%',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Header */}
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // Theme-aware error surface: light mode keeps the filled error.light header; dark
              // mode gets a deep red wash with light-red text — the same tone family as the
              // standard dark-mode error Alert, instead of a salmon block on the dark shell.
              ...(theme.palette.mode === 'dark'
                ? {
                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                    backgroundImage: 'none',
                    color: theme.palette.error.light,
                  }
                : {
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.error.contrastText,
                  }),
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorOutlineIcon />
              <Typography variant="subtitle1" fontWeight={600}>
                {hasErrors ? t('validation.errorsWithCount', { count: errors.length }) : t('validation.errors')}
              </Typography>
            </Box>
            <IconButton
              onClick={toggleDrawer}
              size="small"
              aria-label={t('validation.closePanel')}
              sx={{
                // Follow the header's mode-dependent text color.
                color: 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Paper>

          <Divider />

          {/* Error List */}
          <Box
            sx={(theme) => ({
              flex: 1,
              overflow: 'auto',
              // grey.50 is a fixed near-white — the big white block in dark mode. There, let the
              // drawer's own dark paper show through; light mode keeps the subtle grey tint.
              backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.grey[50],
            })}
          >
            <List sx={{ p: 0 }}>
              {errorEntries.length > 0 ? errorEntries.map(([field, fieldErrors]) => (
                <ErrorEntry key={field} fieldErrors={fieldErrors} schema={schema} t={t} />
              )) : (
                <ListItem>
                  <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">{t('validation.noErrors')}</Typography>
                  </Box>
                </ListItem>
              )}
            </List>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

